import {useConfig} from '@aboutyou/config';
import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {HeroBrandsSection_BrandElement_Products} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/sections.types';
import {
  originClick,
  TrackingPromotionCreative,
} from '@aboutyou/legacy-tracking-sdk';
import {useHistory} from '@aboutyou/router';
import {
  useTrackComponent,
  useTrackHeroBrandSliderAdditionalTilePromotion,
  useTrackOriginClick,
} from '@aboutyou/tracking';
import {
  COLORS,
  EntityList,
  HeroElementSliderProductTile,
  InteractiveSliderProductTileBase,
  mediaTabletAndUp,
  ProductDetailsWrapper,
  SliderWrapper,
  tabletAndUpLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useMemo} from 'react';
import {AdditionalTile} from '../../../../../backend-services/ArticleDetail/Recommendations/AdditionalTile';
import {useSplitFirstProductIds} from '../../../../hooks/useSplitFirstProductIds';
import {ProductTile} from '../../../../ProductTile/ProductTile';
import {AddToBasketPayload} from '../../../../ProductTile/types';
import {HeroContentProductsProps} from './HeroContentSwitch';
import {useShouldTrackProducts} from './utils/useShouldTrackProducts';

export const HeroContentProductsList: React.FC<
  HeroContentProductsProps & {
    products: HeroBrandsSection_BrandElement_Products;
  }
> = ({
  activeBrandIndex,
  brand,
  discoverMore,
  header,
  onAddToBasketCtaClick,
  onClick,
  onProductListSwipe,
  products,
  promotionTracker,
  streamPosition,
  uniqueKey,
  trackComponentConfig,
}) => {
  const {device} = useConfig();
  const history = useHistory();
  const trackOriginClick = useTrackOriginClick();
  const trackComponent = useTrackComponent();
  const trackPromotion = useTrackHeroBrandSliderAdditionalTilePromotion({
    promotion: {
      source: TrackingPromotionCreative.Brand,
      promotionTracker: promotionTracker
        ? {
            ...promotionTracker,
            destination: products.link.url,
          }
        : undefined,
    },
    streamPosition,
    horizontalPosition: activeBrandIndex,
  });

  const handleProductListSwipe = () => {
    onClick();
    onProductListSwipe?.();
  };

  const {isActive: isPriceDisplayOverhaulV2} = useIsFeatureActive(
    'ab_price_display_overhaul_v2',
  );

  const handleAddCtaClick = (payload: AddToBasketPayload) => {
    trackComponent({
      action: 'click',
      content_type: 'button',
      name: 'add_to_basket_product_tile',
      object_type: 'product',
      object_id: payload.product.id,
      location_id:
        promotionTracker.locationId ?? trackComponentConfig.location_id,
      page_type_id: trackComponentConfig.page_type_id,
      creative_name: promotionTracker.creativeName,
      sponsored: promotionTracker.creativeName === 'ad',
      promotion_id: promotionTracker.promotionId,
    });

    onAddToBasketCtaClick?.({
      productId: payload.product.id,
      locationId: promotionTracker.locationId,
      couponPromotionId: payload.couponPromotionId,
    });
  };

  const {shouldDisableTrackProductImpression, onProductImpressionTracked} =
    useShouldTrackProducts(uniqueKey);

  const {linkWithoutFirstProductIds, fullLink} = useSplitFirstProductIds(
    products.link.url,
  );

  const handleAdditionalTileClick: React.MouseEventHandler = event => {
    // fire select promotion event
    trackPromotion('click');

    if (fullLink && fullLink !== linkWithoutFirstProductIds) {
      event.preventDefault();
      history.push(fullLink);
    }
  };

  const renderedProducts = useMemo(() => {
    const nodes = products.products.map((tile, index) => {
      const productTileProps =
        device === 'desktop'
          ? {
              ProductTileComponent: InteractiveSliderProductTileBase,
              onClickAddCta: handleAddCtaClick,
              onMouseEnter: onClick,
            }
          : {ProductTileComponent: StyledHeroElementSliderProductTile};

      return (
        <ProductTile
          key={index}
          tile={{...tile, colors: []}}
          streamPosition={streamPosition}
          disableTracking={shouldDisableTrackProductImpression(tile.productId)}
          onImpressionTracked={() => onProductImpressionTracked(tile.productId)}
          trackingData={{
            ...promotionTracker,
            recoType: promotionTracker.locationId,
            rowIndexInSection: activeBrandIndex,
          }}
          promotionId={
            promotionTracker.creativeName === 'ad'
              ? promotionTracker.promotionId
              : undefined
          }
          {...productTileProps}
        />
      );
    });

    nodes.push(
      <StyledAdditionalTile
        key={products.products.length}
        image={discoverMore.coverImage}
        brand={{name: brand.name, logo: header.darkLogo}}
        label={discoverMore.headline}
        href={linkWithoutFirstProductIds || products.link.url}
        onClick={handleAdditionalTileClick}
      />,
    );

    return nodes;
  }, [products, linkWithoutFirstProductIds, handleAdditionalTileClick]);

  return (
    <ProductsList
      onClick={trackOriginClick(
        (promotionTracker.locationId ??
          trackComponentConfig.location_id) as originClick,
      )}
      data-testid="hero-brand-products-list"
    >
      <StyledEntityList
        uniqueKey={`heroBrandsSliderContent_${brand.id}`}
        type="slider"
        disableHeaderControl
        navButtonsZIndex={2}
        hideSideButtons={device === 'mobile'}
        slideCount={1.7}
        slideCountSmall={2.5}
        slideCountMedium={isPriceDisplayOverhaulV2 ? 3 : 2.5}
        slideCountLarge={3}
        onIndexChange={handleProductListSwipe}
      >
        {renderedProducts}
      </StyledEntityList>
    </ProductsList>
  );
};

const ProductsList = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  z-index: 1;

  ${tabletAndUpLinaria(`
    position: absolute;
    left: 30%;
    z-index: 1;
    width: 70%;
    height: 80%;
    padding-left: 60px;
  `)}
`;

const StyledEntityList = styled(EntityList)`
  z-index: 1;

  @media ${mediaTabletAndUp} {
    z-index: unset;
    ${SliderWrapper} {
      padding-left: 0;
    }
  }
`;

const StyledAdditionalTile = styled(AdditionalTile)`
  background-color: ${COLORS.White};
  border-radius: 6px;

  > div {
    border-radius: 6px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  ${tabletAndUpLinaria(`
    > span {
      padding-block: 14px;
    }
  `)};
`;

const StyledHeroElementSliderProductTile = styled(HeroElementSliderProductTile)`
  ${ProductDetailsWrapper} {
    padding: 10px 8px;
  }
`;
